import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { cn } from 'src/lib/utils';

const Table = ({
  className,
  isLoading,
  maxHeight,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableElement> & {
  isLoading?: boolean;
  maxHeight?: string;
  ref?: React.RefObject<HTMLDivElement | null>;
}) => (
  <div className={cn('relative w-full overflow-auto', maxHeight)} ref={ref}>
    {isLoading && (
      <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-1">
        <Loader2 className="size-6 animate-spin text-foreground" />
      </div>
    )}
    <table className={cn('w-full caption-bottom text-sm', className)} {...props} />
  </div>
);

const TableHeader = ({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <thead className={cn('[&_tr]:border-b', className)} {...props} />
);

const TableBody = ({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />
);

const TableFooter = ({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tfoot className={cn('border-t bg-muted font-medium last:[&>tr]:border-b-0', className)} {...props} />
);

const TableRow = ({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr
    className={cn('border-b transition-colors hover:bg-muted data-[state=selected]:bg-muted', className)}
    {...props}
  />
);

const TableHead = ({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
  <th
    className={cn(
      'h-12 px-3 text-xs text-left uppercase align-middle bg-background-soft font-bold text-slate-700 tracking-[0.04em] [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5',
      className,
    )}
    {...props}
  />
);

const TableCell = ({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
  <td
    className={cn('p-3 align-middle [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5', className)}
    {...props}
  />
);

const TableCaption = ({ className, ...props }: React.HTMLAttributes<HTMLTableCaptionElement>) => (
  <caption className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
);

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
